import {
  Component,
  EventEmitter,
  Inject,
  OnDestroy,
  OnInit,
  Output,
  computed,
  signal,
} from '@angular/core'
import { Subject, takeUntil, debounceTime } from 'rxjs'
import { AppPaths } from 'src/app/app-routing.module'
import { APP_CONFIG } from 'src/app/app.config'
import { AppConfigModel } from 'src/app/core/models'
import { AppStateService } from 'src/app/core/services/app-state.service'
import { floorToMinutes } from 'src/app/shared/utils/date.utils'
import { toSignal } from '@angular/core/rxjs-interop'

@Component({
  selector: 'app-customer-history-control',
  templateUrl: './customer-history-control.component.html',
  styleUrls: ['./customer-history-control.component.scss'],
  standalone: false,
})
export class CustomerHistoryControlComponent implements OnDestroy, OnInit {
  @Output() historyClosed = new EventEmitter<void>()

  public currentTimestamp = signal<Date | undefined>(undefined)
  private cleanUp$ = new Subject<void>()
  public currentTimespan = signal<number | undefined>(undefined)
  public readonly isLoading = toSignal(this.appState.isLoadingChanged$, {
    initialValue: false,
  })
  public zoom = signal<number | undefined>(undefined)
  public readonly zoomLevelAllowedForHistory = computed(() => {
    const zoom = this.zoom()
    return zoom !== undefined && zoom >= this.config.history_min_zoom_level
  })
  private timespanSubject = new Subject<string>()

  constructor(
    private appState: AppStateService,
    @Inject(APP_CONFIG) public readonly config: AppConfigModel,
  ) {
    this.evaluateQueryParameters()

    this.appState.stateChanged.pipe(takeUntil(this.cleanUp$)).subscribe(() => {
      this.evaluateQueryParameters()
    })

    this.updateQueryParameters()

    // Set up throttled timespan handling
    this.timespanSubject
      .pipe(
        debounceTime(20), // Wait 300ms after the last change
        takeUntil(this.cleanUp$),
      )
      .subscribe((timespan) => {
        this.processTimeSpanChange(timespan)
      })
  }

  ngOnInit(): void {
    // First, try to load from query parameters
    this.evaluateQueryParameters()
    // If still no timestamp (not in URL), use default
    if (!this.currentTimestamp()) {
      this.currentTimestamp.set(floorToMinutes(new Date(Date.now())))
    }
    this.currentTimespan.set(
      this.appState.getState().timespan ? this.appState.getState().timespan : 0,
    )
    // Update query parameters to propagate the timestamp change
    this.updateQueryParameters()
  }

  ngOnDestroy(): void {
    this.currentTimestamp.set(undefined)
    this.currentTimespan.set(undefined)
    this.updateQueryParameters()
    this.cleanUp$.next()
    this.cleanUp$.complete()
  }

  handleReloadClick = () => {
    // Needed because of race condition handleOnTimestampChanged
    setTimeout(() => {
      this.appState.reset({ timestamp: undefined })
      this.updateQueryParameters()
    }, 500)
  }

  handleOnHistoryClosed = () => {
    this.historyClosed.emit()
  }

  handleOnTimestampChanged = (timestamp: Date) => {
    this.currentTimestamp.set(timestamp)
  }

  handleOnTimeSpanChanged(timespan: string) {
    // Send to throttled subject instead of processing immediately
    this.timespanSubject.next(timespan)
  }

  private processTimeSpanChange(timespan: string) {
    this.currentTimespan.set(Number(timespan))
    this.updateQueryParameters()
  }

  updateQueryParameters() {
    this.appState.setState(AppPaths.Customer, {
      timestamp: this.currentTimestamp()?.toISOString(),
      timespan: this.currentTimespan(),
    })
  }

  evaluateQueryParameters() {
    const zoom = this.appState.getState().zoom
    this.zoom.set(zoom !== undefined ? Math.floor(Number(zoom)) : undefined)
    this.currentTimespan.set(this.appState.getState().timespan)
    const selectedTimestamp = this.appState.selectTimestamp()
    this.currentTimestamp.set(selectedTimestamp ? selectedTimestamp : this.currentTimestamp())
  }
}
